import { Children, ReactNode, useContext, useEffect, ReactElement, useState } from "react";
import { useModulePathResolver } from "../../hooks";
import { BreadcrumbContext, RegisteredBreadcrumbData } from "./BreadcrumbContext";

/** The `Breadcrumb` component properties. */
export interface BreadcrumbProps {
  
  /** The breadcrumb title. */
  readonly title: string;

  /** The breadcrumb path. */
  readonly path: string;

  /** The component children. */
  readonly children?: ReactNode;
}

/**
 * Indicates whether the specified React node is a Breadcrumb.
 * @param node The node to test.
 * @returns True if the specified node is a breadcrumb.
 */
function isBreadcrumb(node: ReactNode): node is ReactElement<BreadcrumbProps> {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  if ((node as ReactElement).type && (node as ReactElement).type === Breadcrumb) return true;

  return false;
}

/** The breadcrumb component. */
export const Breadcrumb = (props: BreadcrumbProps): ReactNode => {
  const { setItems } = useContext(BreadcrumbContext);
  const resolvePath = useModulePathResolver();
  const [internalItems, setInternalItems] = useState<ReadonlyArray<RegisteredBreadcrumbData>>([]);

  useEffect(() => {
    const items: Array<RegisteredBreadcrumbData> = [];

    function processBreadcrumb(parentIndex: number, props: BreadcrumbProps): void {
      const { title, path, children } = props;

      const index = items.length + 1; // Start counting from 1.
      items.push({ title, path: resolvePath(path), parentIndex });

      Children.forEach(children, (child) => {
        if (!isBreadcrumb(child)) return;
        processBreadcrumb(index, child.props);
      });
    }

    processBreadcrumb(0, props); // Index 0 is "Home".

    setInternalItems((previousItems) => {
      if (previousItems.length !== items.length) return items;

      for (let i = 0; i < previousItems.length; i++) {
        const previous = previousItems[i];
        const next = items[i];

        if (previous.parentIndex !== next.parentIndex || previous.path !== next.path || previous.title !== next.title) return items;
      }

      return previousItems;
    });
  });

  useEffect(() => {
    return setItems(internalItems);
  }, [internalItems]);

  return null;
};
